import React, { useEffect } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { CheckCircle2, Phone, Clock } from 'lucide-react';
import PageShell from '@/components/PageShell';
import SEO from '@/components/SEO';
import { useLanguage } from '@/i18n/LanguageContext';
import { track } from '@/lib/analytics';

const ThankYou = () => {
  const { lang } = useLanguage();
  const location = useLocation();
  const { referenceId, callMeNow } = location.state || {};

  useEffect(() => { if (referenceId) track('application_completed', { referenceId }); }, [referenceId]);

  if (!referenceId) {
    return <Navigate to="/start-a-bond" replace />;
  }

  return (
    <PageShell trackPage="/thank-you">
      <SEO title="Submission Received | Amigo's Bail Bonds" description="Your bail bond request has been received." path="/thank-you" />
      <section className="section section--void" style={{ minHeight: '70vh', display: 'flex', alignItems: 'center' }}>
        <div className="container text-center" style={{ maxWidth: 640 }}>
          <CheckCircle2 size={56} color="var(--success)" style={{ marginBottom: '1.2rem' }} />
          <h1 className="section-title">{lang === 'es' ? 'SOLICITUD RECIBIDA' : 'REQUEST RECEIVED'}</h1>
          <p className="section-subtitle" style={{ margin: '0 auto 1.6rem' }}>
            {lang === 'es'
              ? 'Hemos recibido su solicitud y un agente se pondrá en contacto con usted en breve.'
              : "We've received your request and an agent will reach out to you shortly."}
          </p>
          <div className="info-card mb-8" style={{ display: 'inline-block', textAlign: 'left' }} data-testid="reference-id-card">
            <span style={{ fontFamily: 'var(--font-alt)', fontSize: '0.72rem', letterSpacing: '0.08em', color: 'var(--text-dim)', textTransform: 'uppercase' }}>{lang === 'es' ? 'ID DE REFERENCIA' : 'REFERENCE ID'}</span>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--gold)' }} data-testid="reference-id-value">{referenceId}</div>
          </div>

          {callMeNow && (
            <div className="urgent-strip mb-8" style={{ display: 'inline-flex' }}>
              <Clock size={18} color="#fff" />
              <p>{lang === 'es' ? 'Marcado como urgente — le llamaremos lo antes posible.' : "Flagged as urgent — we'll call you as soon as possible."}</p>
            </div>
          )}

          <div className="flex gap-4" style={{ justifyContent: 'center', flexWrap: 'wrap', marginBottom: '2rem' }}>
            <a href="tel:9368282500" className="btn-red" onClick={() => track('phone_click', { location: 'thank_you' })} data-testid="thankyou-call-btn">
              <Phone size={16} /> {lang === 'es' ? 'Para ayuda más rápida, llame ahora' : 'For fastest help, call now'}
            </a>
            <Link to="/" className="btn-ghost-dark" data-testid="thankyou-home-link">{lang === 'es' ? 'Volver al inicio' : 'Back to Home'}</Link>
          </div>

          <div className="timeline text-left" style={{ maxWidth: 420, margin: '0 auto', textAlign: 'left' }}>
            <div className="timeline-item"><strong style={{ color: 'var(--text-primary)' }}>{lang === 'es' ? 'Solicitud enviada' : 'Request submitted'}</strong><div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{lang === 'es' ? 'Justo ahora' : 'Just now'}</div></div>
            <div className="timeline-item"><strong style={{ color: 'var(--text-primary)' }}>{lang === 'es' ? 'Un agente revisará su caso' : 'An agent will review your case'}</strong><div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{lang === 'es' ? 'En breve' : 'Shortly'}</div></div>
            <div className="timeline-item"><strong style={{ color: 'var(--text-primary)' }}>{lang === 'es' ? 'Le contactaremos' : "We'll reach out to you"}</strong><div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{lang === 'es' ? 'Por llamada o texto, según su preferencia' : 'By call or text, per your preference'}</div></div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default ThankYou;
